import React, { createContext, useEffect, useMemo, useState } from 'react'

import { getActiveLanguage } from 'react-localize-redux'
import { connect } from 'react-redux'
import { getUser } from '../reducers/UserReducer'
import { fetchSchoolOptions, getAllData } from '../utils/api/school'
import { getInstitutionSectors } from '../utils/api/institution'
import { SchoolOptions } from '../utils/constants'
import { isObject } from '../utils'

const { DRAFT_INTERNSHIPS_TYPE, STUDENT_HOURS_WEIGHTED_EXPORT, EXPORT_HOURS } = SchoolOptions

const mapStateToProps = state => ({
  currentLanguage: getActiveLanguage(state.locale).code,
  user: getUser(state.getUser)
})

export const SchoolContext = createContext()

const SchoolProvider = ({ children, currentLanguage, user }) => {
  const [actTypeCategories, setActTypeCategories] = useState([])
  const [school, setSchool] = useState(null)
  const [institutionConstraints, setInstitutionConstraints] = useState([])
  const [institutions, setInstitutions] = useState([])
  const [schoolOptions, setSchoolOptions] = useState([])
  const [schoolPermissions, setSchoolPermissions] = useState({})
  const [schoolSections, setSchoolSections] = useState([])
  const [sectorsByInstitution, setSectorsByInstitution] = useState({})

  const [institutionSectorsLoading, setInstitutionSectorsLoading] = useState({})
  const [loading, setLoading] = useState(true)

  const sections = useMemo(() => schoolSections.reduce((acc, ss) => {
    if (isObject(ss.sectionToYear)) {
      const section = ss.sectionToYear.section

      if (!acc.sectionYears[section.id]) {
        acc.sectionYears[section.id] = []
        acc.list.push(section)
      }

      if (!acc.sectionYears[section.id].some(sty => sty.id === ss.sectionToYear.id)) {
        acc.sectionYears[section.id].push(ss.sectionToYear)
      }
    }

    return acc
  }, { list: [], sectionYears: {} }), [schoolSections])

  const useDraftInternships = useMemo(() => {
    let use = false

    for (const so of schoolOptions) {
      if (so.optionType.type === DRAFT_INTERNSHIPS_TYPE) {
        use = so.optionEnabled
        break
      }
    }

    return use
  }, [schoolOptions])
  const useHoursExport = useMemo(() => {
    let use = false

    for (const so of schoolOptions) {
      if (so.optionType.type === EXPORT_HOURS) {
        use = so.optionEnabled
        break
      }
    }

    return use
  }, [schoolOptions])
  const useWeightedHours = useMemo(() => {
    let use = false

    for (const so of schoolOptions) {
      if (so.optionType.type === STUDENT_HOURS_WEIGHTED_EXPORT) {
        use = so.optionEnabled
        break
      }
    }

    return use
  }, [schoolOptions])

  const unmanagedInstitutions = useMemo(() => institutions.filter(i => !i.managed), [institutions])

  useEffect(() => {
    if (user.school) {
      setLoading(true)

      getAllData(user, user.school).then(json => {
        if (json?.data) {
          setActTypeCategories(json.data.actTypeCategories)
          setSchool(json.data.school)
          setInstitutionConstraints(json.data.institutionConstraints)
          setInstitutions(json.data.institutions)
          setSchoolOptions(json.data.schoolOptions)
          setSchoolPermissions(json.data.schoolPermissions)
          setSchoolSections(json.data.schoolSections)
        }

        setLoading(false)
      })
    } else {
      setLoading(true)
      setSchool(null)
    }
  }, [user.school])

  const reloadSchoolOptions = user => {
    fetchSchoolOptions(user, { id: user.school.id }).then(json => {
      if (json?.data) {
        setSchoolOptions(json.data)
      }
    })
  }

  const loadInstitutionSectors = institution => {
    switchInstitutionSectorsLoading(true, institution.id)

    getInstitutionSectors(user, institution).then(json => {
      if (json?.data) {
        const updatedSectors = { ...sectorsByInstitution }

        updatedSectors[institution.id] = json.data

        setSectorsByInstitution(updatedSectors)
      }

      switchInstitutionSectorsLoading(false, institution.id)
    })
  }

  const switchInstitutionSectorsLoading = (value, institutionId) => {
    const newLoadings = { ...institutionSectorsLoading }

    newLoadings[institutionId] = value

    setInstitutionSectorsLoading(newLoadings)
  }

  return (
    <SchoolContext.Provider
      value={{
        actTypeCategories,
        institutionConstraints,
        institutions,
        sections,
        school,
        schoolOptions,
        schoolPermissions,
        schoolSections,
        sectorsByInstitution,
        unmanagedInstitutions,
        useDraftInternships,
        useHoursExport,
        useWeightedHours,
        institutionSectorsLoading,
        schoolLoading: loading,
        loadInstitutionSectors,
        reloadSchoolOptions,
        setActTypeCategories,
        setInstitutions,
        setSectorsByInstitution
      }}
    >
      {children}
    </SchoolContext.Provider>
  )
}

export default connect(mapStateToProps)(SchoolProvider)
